<template>
  <div class="pwa-report">
    <!-- 헤더 -->
    <div class="report-header">
      <div class="header-row">
        <span class="page-title">보고서</span>
        <button class="ym-btn" @click="showYearMonthPicker = true">
          {{ formatYearMonth(selectedYearMonth) }}
          <v-icon size="16">mdi-chevron-down</v-icon>
        </button>
      </div>
    </div>

    <!-- 빠른 템플릿 -->
    <div class="templates">
      <div class="template-grid">
        <button 
          v-for="template in reportTemplates" 
          :key="template.id"
          class="template-btn"
          @click="generateReport(template)"
        >
          <v-icon size="20" :color="template.color">{{ template.icon }}</v-icon>
          <span>{{ template.name }}</span>
        </button>
      </div>
    </div>

    <!-- 보고서 리스트 -->
    <div class="list-section">
      <div class="list-header">
        <span>생성된 보고서</span>
        <span class="count">{{ reports.length }}건</span>
      </div>

      <div class="report-list">
        <!-- 로딩 -->
        <template v-if="isLoading">
          <div v-for="n in 4" :key="n" class="report-item skeleton">
            <div class="skeleton-icon"></div>
            <div class="skeleton-text"></div>
          </div>
        </template>

        <!-- 빈 상태 -->
        <div v-else-if="reports.length === 0" class="empty-state">
          <v-icon size="40" color="#d1d5db">mdi-file-document-outline</v-icon>
          <span>보고서가 없습니다</span>
        </div>

        <!-- 리스트 -->
        <template v-else>
          <div
            v-for="report in reports"
            :key="report.id"
            class="report-item"
          >
            <div class="icon" :class="getTypeClass(report.type)">
              <v-icon size="18" color="white">{{ getIcon(report.type) }}</v-icon>
            </div>
            <div class="info">
              <span class="name">{{ report.name }}</span>
              <span class="meta">{{ report.type }} · {{ formatDate(report.createdDate) }}</span>
            </div>
            <button class="download-btn" @click="downloadReport(report)">
              <v-icon size="18">mdi-download</v-icon>
            </button>
          </div>
        </template>
      </div>
    </div>

    <!-- 연월 선택 -->
    <v-dialog v-model="showYearMonthPicker" max-width="300">
      <v-card rounded="lg">
        <div class="ym-picker">
          <div class="year-row">
            <button @click="yearMonth.year--"><v-icon>mdi-chevron-left</v-icon></button>
            <span>{{ yearMonth.year }}년</span>
            <button @click="yearMonth.year++"><v-icon>mdi-chevron-right</v-icon></button>
          </div>
          <div class="month-grid">
            <button
              v-for="m in 12"
              :key="m"
              :class="{ active: yearMonth.month === m }"
              @click="selectMonth(m)"
            >{{ m }}월</button>
          </div>
        </div>
      </v-card>
    </v-dialog>

    <!-- 생성 확인 -->
    <v-dialog v-model="showGenerateDialog" max-width="300">
      <v-card rounded="lg">
        <div class="confirm-dialog">
          <div class="dialog-icon" :class="selectedTemplate?.type">
            <v-icon size="24" color="white">{{ selectedTemplate?.icon }}</v-icon>
          </div>
          <div class="dialog-title">{{ selectedTemplate?.name }}</div>
          <div class="dialog-desc">{{ formatYearMonth(selectedYearMonth) }} 보고서 생성</div>
          <div class="dialog-actions">
            <button class="btn secondary" @click="showGenerateDialog = false">취소</button>
            <button class="btn primary" @click="confirmGenerate" :disabled="isGenerating">
              <v-progress-circular v-if="isGenerating" indeterminate size="14" width="2"></v-progress-circular>
              <span v-else>생성</span>
            </button>
          </div>
        </div>
      </v-card>
    </v-dialog>

    <LodingComponent :is-visible="isLoading" text="보고서를 불러오는 중..." />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import axios from 'axios';
import LodingComponent from '@/components/loading/LodingComponent.vue';

interface Report {
  id: number;
  name: string;
  type: string;
  createdDate: string;
  downloadUrl?: string;
}

interface ReportTemplate {
  id: number;
  name: string;
  icon: string;
  color: string;
  type: string;
}

const store = useStore();
const user = computed(() => store.state.user);

const isLoading = ref(false);
const isGenerating = ref(false);
const showYearMonthPicker = ref(false);
const showGenerateDialog = ref(false);
const selectedTemplate = ref<ReportTemplate | null>(null);

const getLastMonth = () => {
  const now = new Date();
  const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  return { year: lastMonth.getFullYear(), month: lastMonth.getMonth() + 1 };
};

const yearMonth = ref(getLastMonth());
const selectedYearMonth = computed(() => 
  `${yearMonth.value.year}${String(yearMonth.value.month).padStart(2, '0')}`
);

const reports = ref<Report[]>([]);

const reportTemplates: ReportTemplate[] = [
  { id: 1, name: '급여명세서', icon: 'mdi-file-document', color: '#1e3a8a', type: '급여명세서' },
  { id: 2, name: '인건비', icon: 'mdi-chart-line', color: '#059669', type: '인건비보고서' },
  { id: 3, name: '직원현황', icon: 'mdi-account-group', color: '#7c3aed', type: '직원현황' },
  { id: 4, name: '결산', icon: 'mdi-calculator', color: '#dc2626', type: '결산보고서' }
];

const fetchReports = async () => {
  if (!user.value?.m_sisul_code) return;
  try {
    isLoading.value = true;
    const response = await axios.get(`/api/reports/${user.value.m_sisul_code}/${selectedYearMonth.value}`, { withCredentials: true });

    if (response.data.success) {
      reports.value = response.data.data || [];
    } else {
      reports.value = [
        { id: 1, name: `${yearMonth.value.month}월 급여명세서`, type: '급여명세서', createdDate: new Date().toISOString() },
        { id: 2, name: `${yearMonth.value.month}월 인건비 보고서`, type: '인건비보고서', createdDate: new Date().toISOString() }
      ];
    }
  } catch (error) {
    console.error('보고서 조회 오류:', error);
    reports.value = [
      { id: 1, name: `${yearMonth.value.month}월 급여명세서`, type: '급여명세서', createdDate: new Date().toISOString() }
    ];
  } finally {
    isLoading.value = false;
  }
};

const formatYearMonth = (ym: string) => {
  if (!ym || ym.length !== 6) return '';
  return `${ym.substring(0, 4)}.${parseInt(ym.substring(4, 6))}`;
};

const formatDate = (dateStr: string) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return `${date.getMonth() + 1}/${date.getDate()}`;
};

const selectMonth = (month: number) => {
  yearMonth.value.month = month;
  showYearMonthPicker.value = false;
  fetchReports();
};

const getTypeClass = (type: string) => {
  const map: Record<string, string> = {
    '급여명세서': 'wage', '인건비보고서': 'cost', '직원현황': 'employee', '결산보고서': 'settlement'
  };
  return map[type] || 'default';
};

const getIcon = (type: string) => {
  const map: Record<string, string> = {
    '급여명세서': 'mdi-file-document', '인건비보고서': 'mdi-chart-line', 
    '직원현황': 'mdi-account-group', '결산보고서': 'mdi-calculator'
  };
  return map[type] || 'mdi-file-document';
};

const generateReport = (template: ReportTemplate) => {
  selectedTemplate.value = template;
  showGenerateDialog.value = true;
};

const confirmGenerate = async () => {
  if (!selectedTemplate.value || !user.value?.m_sisul_code) return;
  try {
    isGenerating.value = true;
    const response = await axios.post('/api/reports/generate', {
      sisulCode: user.value.m_sisul_code,
      yearMonth: selectedYearMonth.value,
      type: selectedTemplate.value.type
    }, { withCredentials: true });

    if (response.data.success) {
      store.commit('showSnackbar', { text: '보고서가 생성되었어요!', color: 'success' });
      fetchReports();
    } else {
      const newReport: Report = {
        id: Date.now(),
        name: `${yearMonth.value.month}월 ${selectedTemplate.value.name}`,
        type: selectedTemplate.value.type,
        createdDate: new Date().toISOString()
      };
      reports.value.unshift(newReport);
      store.commit('showSnackbar', { text: '보고서가 생성되었어요!', color: 'success' });
    }
  } catch (error) {
    const newReport: Report = {
      id: Date.now(),
      name: `${yearMonth.value.month}월 ${selectedTemplate.value!.name}`,
      type: selectedTemplate.value!.type,
      createdDate: new Date().toISOString()
    };
    reports.value.unshift(newReport);
    store.commit('showSnackbar', { text: '보고서가 생성되었어요!', color: 'success' });
  } finally {
    isGenerating.value = false;
    showGenerateDialog.value = false;
  }
};

const downloadReport = (report: Report) => {
  store.commit('showSnackbar', { text: `${report.name} 다운로드`, color: 'info' });
  if (report.downloadUrl) window.open(report.downloadUrl, '_blank');
};

onMounted(() => fetchReports());
</script>

<style scoped lang="scss">
@import '@/assets/styles/fonts.scss';

.pwa-report {
  min-height: 100vh;
  background: #fff;
  padding-bottom: calc(70px + env(safe-area-inset-bottom, 0px));
  font-family: $Noto_Sans_KR !important;
  * { font-family: $Noto_Sans_KR !important; }

  .report-header {
    position: sticky;
    top: 0;
    z-index: 100;
    background: #fff;
    padding: 12px 16px;
    padding-top: calc(12px + env(safe-area-inset-top, 0px));
    border-bottom: 1px solid #f0f0f0;

    .header-row {
      display: flex;
      align-items: center;
      justify-content: space-between;

      .page-title { font-size: 22px; font-weight: 700; }
      .ym-btn {
        display: flex;
        align-items: center;
        gap: 2px;
        padding: 6px 10px;
        background: #f5f5f5;
        border: none;
        border-radius: 6px;
        font-size: 14px;
        font-weight: 600;
        color: #333;
      }
    }
  }

  .templates {
    padding: 12px 16px;
    border-bottom: 1px solid #f0f0f0;

    .template-grid {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 8px;

      .template-btn {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 6px;
        padding: 12px 8px;
        background: #f9f9f9;
        border: none;
        border-radius: 10px;
        cursor: pointer;

        &:active { background: #f0f0f0; }

        span { font-size: 11px; color: #333; font-weight: 500; }
      }
    }
  }

  .list-section {
    .list-header {
      display: flex;
      justify-content: space-between;
      padding: 12px 16px;
      font-size: 14px;
      font-weight: 600;
      color: #333;

      .count { color: #999; font-weight: 400; }
    }
  }

  .report-list {
    .report-item {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 12px 16px;
      border-bottom: 1px solid #f5f5f5;

      &.skeleton {
        .skeleton-icon { width: 36px; height: 36px; border-radius: 8px; background: #f0f0f0; }
        .skeleton-text { height: 14px; width: 120px; background: #f0f0f0; border-radius: 4px; }
      }

      .icon {
        width: 36px;
        height: 36px;
        border-radius: 8px;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;

        &.wage { background: #1e3a8a; }
        &.cost { background: #059669; }
        &.employee { background: #7c3aed; }
        &.settlement { background: #dc2626; }
        &.default { background: #64748b; }
      }

      .info {
        flex: 1;
        min-width: 0;
        display: flex;
        flex-direction: column;

        .name { font-size: 14px; font-weight: 600; color: #111; }
        .meta { font-size: 12px; color: #999; }
      }

      .download-btn {
        width: 36px;
        height: 36px;
        background: #f5f5f5;
        border: none;
        border-radius: 8px;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #1e3a8a;

        &:active { background: #e0e0e0; }
      }
    }
  }

  .empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 60px 20px;
    color: #999;
    font-size: 14px;
    gap: 8px;
  }

  // 연월 선택
  .ym-picker {
    padding: 20px;

    .year-row {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 16px;
      margin-bottom: 16px;

      button {
        width: 32px;
        height: 32px;
        background: #f5f5f5;
        border: none;
        border-radius: 6px;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      span { font-size: 17px; font-weight: 700; }
    }

    .month-grid {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 6px;

      button {
        padding: 12px 0;
        border: 1px solid #e0e0e0;
        border-radius: 6px;
        background: #fff;
        font-size: 14px;
        color: #666;

        &.active {
          background: #1e3a8a;
          border-color: #1e3a8a;
          color: #fff;
          font-weight: 600;
        }
      }
    }
  }

  // 확인 다이얼로그
  .confirm-dialog {
    padding: 24px;
    text-align: center;

    .dialog-icon {
      width: 56px;
      height: 56px;
      border-radius: 14px;
      display: flex;
      align-items: center;
      justify-content: center;
      margin: 0 auto 12px;

      &.급여명세서 { background: #1e3a8a; }
      &.인건비보고서 { background: #059669; }
      &.직원현황 { background: #7c3aed; }
      &.결산보고서 { background: #dc2626; }
    }

    .dialog-title { font-size: 17px; font-weight: 700; margin-bottom: 4px; }
    .dialog-desc { font-size: 13px; color: #888; margin-bottom: 20px; }

    .dialog-actions {
      display: flex;
      gap: 10px;

      .btn {
        flex: 1;
        height: 44px;
        border-radius: 8px;
        font-size: 15px;
        font-weight: 600;
        border: none;
        cursor: pointer;

        &.primary { background: #1e3a8a; color: #fff; }
        &.secondary { background: #f5f5f5; color: #333; }
      }
    }
  }
}
</style>
